<template>
    <Dialog v-if="devToolsEnabled" v-model:open="open">
        <DialogContent size="sm">
            <DialogHeader>
                <DialogTitle>Feature flags</DialogTitle>
                <DialogDescription>
                    Dev builds only (Ctrl+Shift+K). Overrides apply to this browser and reload the app.
                </DialogDescription>
            </DialogHeader>

            <section aria-label="Feature flags" class="gap-sm flex flex-col">
                <div
                    v-for="(definition, name) in FEATURE_FLAGS"
                    :key="name"
                    class="gap-sm flex items-center justify-between"
                >
                    <div class="min-w-0">
                        <p class="text-sm">{{ definition.label }}</p>
                        <p class="text-xs text-(--text-muted)">
                            {{ definition.description }} Build default:
                            {{ getFeatureDefault(name) ? 'on' : 'off' }}.
                        </p>
                    </div>
                    <fieldset
                        class="gap-2xs p-2xs flex shrink-0 items-center rounded-md bg-(--surface-highlight)"
                        role="radiogroup"
                        :aria-label="`${definition.label} override`"
                    >
                        <Button
                            v-for="choice in overrideChoices"
                            :key="choice.label"
                            variant="ghost"
                            size="xs"
                            role="radio"
                            :aria-checked="getFeatureOverride(name) === choice.value"
                            :class="
                                getFeatureOverride(name) === choice.value
                                    ? 'bg-negative text-negative hover:bg-negative'
                                    : 'bg-transparent text-primary'
                            "
                            @click="applyOverride(name, choice.value)"
                        >
                            {{ choice.label }}
                        </Button>
                    </fieldset>
                </div>
            </section>

        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    FEATURE_FLAGS,
    type FeatureFlagName,
    getFeatureDefault,
    getFeatureOverride,
    isDevToolsEnabled,
    setFeatureOverride,
} from '@/config/featureFlags';

// Indirection so unit tests can observe the reload without fighting
// jsdom's non-configurable window.location.
const props = withDefaults(defineProps<{ reload?: () => void }>(), {
    reload: () => window.location.reload(),
});

const devToolsEnabled = isDevToolsEnabled();
const open = ref(false);

const overrideChoices: Array<{ label: string; value: boolean | null }> = [
    { label: 'Default', value: null },
    { label: 'On', value: true },
    { label: 'Off', value: false },
];

function applyOverride(name: FeatureFlagName, value: boolean | null) {
    if (getFeatureOverride(name) === value) {
        return;
    }
    setFeatureOverride(name, value);
    props.reload();
}

function onKeydown(event: KeyboardEvent) {
    if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        open.value = !open.value;
    }
}

onMounted(() => {
    if (devToolsEnabled) {
        window.addEventListener('keydown', onKeydown);
    }
});
onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeydown);
});
</script>
